import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import NavigationBar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import ArticleCard from "@/components/base/ArticleCard";
import CategorySidebar from "@/pages/category/components/CategorySidebar";
import { useSeo } from "@/hooks/useSeo";
import {
  allCategory,
  getArticlesByCategory,
  getCategory,
  searchArticles,
  siteInfo,
  sortArticles,
  type Article,
} from "@/utils/content";

export default function CategoryPage() {
  const { slug = "all" } = useParams();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";

  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [keyword, setKeyword] = useState(q);
  const [visible, setVisible] = useState(5);

  const category = slug === "all" ? allCategory : getCategory(slug);
  const matched = Boolean(category);
  const current = category || allCategory;

  useEffect(() => {
    setKeyword(q);
    setVisible(5);
  }, [q, slug]);

  const baseList: Article[] = getArticlesByCategory(matched ? slug : "all");
  const filtered = searchArticles(baseList, q);
  const sorted = sortArticles(filtered, sort);
  const shown = sorted.slice(0, visible);

  const tags = Array.from(new Set(baseList.flatMap((item) => item.tags))).slice(0, 12);

  useSeo({
    title: `${current.name} - 分类导航 | 511爆料`,
    description: `${current.name}分类导航页，${current.desc} 共收录 ${current.count} 条内容，支持关键词搜索与最新、最热排序。`,
    keywords: `511爆料,${current.name},511爆料${current.name},511爆料分类导航`,
    canonical: `/category/${current.slug}`,
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${current.name} - 511爆料`,
        description: current.desc,
        url: `${siteInfo.baseUrl}/category/${current.slug}`,
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: "511爆料", url: siteInfo.baseUrl },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.baseUrl },
          { "@type": "ListItem", position: 2, name: current.name, item: `${siteInfo.baseUrl}/category/${current.slug}` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${current.name}内容列表`,
        itemListElement: sorted.slice(0, 10).map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `${siteInfo.baseUrl}/article/${item.id}`,
        })),
      },
    ],
  });

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const kw = keyword.trim();
    if (kw) {
      setParams({ q: kw });
    } else {
      setParams({});
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <NavigationBar />
      <main className="flex-1">
        <PageHeader
          eyebrow="分类导航"
          title={`${current.name}${q ? ` · 搜索「${q}」` : ""}`}
          description={current.desc}
          crumbs={[{ label: "分类导航", to: "/category/all" }, { label: current.name }]}
        />

        {!matched && (
          <div className="mx-auto max-w-6xl px-4 pt-8 md:px-8">
            <p className="rounded-lg border border-accent-300 bg-accent-50 px-5 py-4 text-sm text-accent-900">
              没有找到对应的分类，已为你展示全部内容。
            </p>
          </div>
        )}

        <section className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 md:px-8 md:py-14 lg:flex-row">
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between">
              <form onSubmit={handleSearch} className="flex flex-1 items-center gap-2">
                <div className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 md:max-w-sm">
                  <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                    <i className="ri-search-line text-base" />
                  </span>
                  <input
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder={`在${current.name}中搜索`}
                    className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                    aria-label={`在${current.name}中搜索关键词`}
                  />
                </div>
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索
                </button>
              </form>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
                  {([
                    { key: "latest", label: "最新" },
                    { key: "hot", label: "最热" },
                  ] as const).map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSort(item.key)}
                      className={`whitespace-nowrap rounded-full px-3.5 py-1 text-sm font-medium transition-colors ${
                        sort === item.key ? "bg-foreground-950 text-background-50" : "text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-4 text-sm text-foreground-500">
              共找到 <strong className="font-semibold text-foreground-900">{sorted.length}</strong> 条内容
              {q && (
                <>
                  ，关键词「{q}」
                  <button
                    type="button"
                    onClick={() => {
                      setKeyword("");
                      setParams({});
                    }}
                    className="ml-2 text-primary-600 hover:underline"
                  >
                    清除
                  </button>
                </>
              )}
            </p>

            {shown.length > 0 ? (
              <>
                <div className="mt-6 flex flex-col gap-5">
                  {shown.map((item) => (
                    <ArticleCard key={item.id} article={item} variant="wide" />
                  ))}
                </div>

                {visible < sorted.length ? (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + 4)}
                      className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      加载更多（还有 {sorted.length - visible} 条）
                    </button>
                  </div>
                ) : (
                  <p className="mt-8 text-center text-xs text-foreground-500">已经到底啦，换个分类继续逛逛？</p>
                )}
              </>
            ) : (
              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-10 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-500">
                  <i className="ri-search-eye-line" />
                </span>
                <p className="mt-4 text-sm text-foreground-700">没有找到匹配的内容，换个关键词试试？</p>
                <Link to="/category/all" className="mt-4 inline-flex text-sm font-semibold text-primary-600 hover:underline">
                  返回全部爆料
                </Link>
              </div>
            )}
          </div>

          <CategorySidebar activeSlug={matched ? slug : "all"} tags={tags} />
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}